<script setup>
import { VDataTableServer } from "vuetify/labs/VDataTable";
import AppSelect from "@core/components/app-form-elements/AppSelect.vue";
import moment from "moment-timezone";

moment.tz.setDefault("Asia/Jakarta");

const loading = ref();
const loadingTable = ref();
const loadingClear = ref();

definePage({
  meta: {
    layout: "default",
  },
});
const pageTitle = "Data Location";

const breadCrumbs = [
  {
    title: "Dashboard",
    to: "/admin/dashboard",
  },
  {
    title: "Master Data",
    to: null,
  },
  {
    title: pageTitle,
    to: null,
  },
];
const headers = [
  {
    title: "Location ID",
    key: "id",
  },
  {
    title: "Location Name",
    key: "name",
  },
  {
    title: "Status",
    key: "status",
  },
  {
    title: "Inst. times",
    key: "installTime",
  },
  {
    title: "Shop",
    key: "shopName",
  },
  {
    title: "Last open date",
    key: "lastOpen",
  },
  {
    title: "Actions",
    sortable: false,
    key: "actions",
  },
];

const route = useRoute();
const router = useRouter();

const refForm = ref();

const valid = ref("");
const errors = ref("");

const statusesTitle = {
  11: "Installation scheduled",
  21: "Open",
  22: "Close",
  23: "Released",
  31: "Blocked",
  41: "Suspicious",
  51: "Removed",
};

const statuses = [
  {
    title: "Installation scheduled",
    value: 11,
  },
  {
    title: "Open",
    value: 21,
  },
  {
    title: "Close",
    value: 22,
  },
  {
    title: "Released",
    value: 23,
  },
  {
    title: "Blocked",
    value: 31,
  },
  {
    title: "Suspicious",
    value: 41,
  },
  {
    title: "Removed",
    value: 51,
  },
];

// Data table options
const itemsPerPage = ref(10);
const page = ref(1);
const orderBy = ref("createdAt");
const orderDirection = ref("ASC");

const searchByName = ref(null);
const searchByMerchantId = ref(null);
const searchByMerchantIdParse = ref(null);

const searchByShopId = ref(null);
const searchByShopIdParse = ref(null);

const searchByLocationId = ref(null);
const searchByStatus = ref(null);

const { data: dataQueries, execute: fetchQueries } = await useApi(
  createUrl("/cms/location", {
    query: {
      limit: itemsPerPage,
      searchByName: searchByName,
      searchByMerchantId: searchByMerchantIdParse,
      searchByShopId: searchByShopId,
      searchByLocationId: searchByLocationId,
      searchByStatus: searchByStatus,
      page,
      orderBy,
      orderDirection,
    },
  }),
);

const updateOptions = async (options) => {
  loadingTable.value = true;
  page.value = options.page;
  orderBy.value = options.sortBy[0]?.key;
  orderDirection.value = options.sortBy[0]?.order;
  fetchQueries().then((value) => {
    loadingTable.value = false;
  });
};

const perPages = [5, 10, 25, 50];

const refreshResult = () => {
  loadingClear.value = true;
  loadingTable.value = true;

  searchByName.value = null;
  searchByMerchantId.value = null;
  searchByMerchantIdParse.value = null;

  searchByShopId.value = null;
  searchByShopIdParse.value = null;

  searchByLocationId.value = null;
  searchByStatus.value = null;

  fetchQueries().then((value) => {
    loadingClear.value = false;
    loadingTable.value = false;
  });
};

const searchResult = () => {
  loading.value = true;
  loadingTable.value = true;
  if (searchByMerchantId.value) {
    searchByMerchantIdParse.value = searchByMerchantId.value.split(" : ", 1)[0];
  } else {
    searchByMerchantIdParse.value = "";
  }
  if (searchByShopId.value) {
    searchByShopIdParse.value = searchByShopId.value.split(" : ", 1)[0];
  } else {
    searchByShopIdParse.value = "";
  }
  refForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) {
      fetchQueries().then((value) => {
        loading.value = false;
        loadingTable.value = false;
      });
    }
  });
};

const dataItems = computed(() => (dataQueries ? dataQueries.value?.data : []));
const totalItems = computed(() =>
  dataQueries ? dataQueries.value?.metadata.pagination.itemCount : [],
);

const referMerchant = ref(false);
const referMerchantClick = (item) => {
  referMerchant.value = item;
};
const referLocation = ref(false);
const referLocationClick = (item) => {
  referLocation.value = item;
};
const referShop = ref(false);
const referShopClick = (item) => {
  referShop.value = item;
};

/*
 * Delete
 * */
const deleteDialogDisplay = ref("");
const deleteDialog = ref(false);
const deleteAlert = ref(false);
const deleteItem = (e, name) => {
  deleteDialog.value = e;
  deleteDialogDisplay.value = name;
};
const deleteOk = async (e) => {
  loadingTable.value = true;
  try {
    const res = await $api("/cms/location/" + e, {
      method: "DELETE",
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200) {
      deleteAlert.value = res.message;
    }
  } catch (err) {
    deleteAlert.value = `The record count not be deleted, because of an association`;
    errors.value = err.message;
  }
  fetchQueries().then((value) => {
    loadingTable.value = false;
    deleteAlert.value = false;
    errors.value = null;
    deleteDialog.value = false;
  });
};
const deleteCancel = () => {
  deleteDialog.value = false;
  deleteDialogDisplay.value = "";
};
</script>

<template>
  <VBreadcrumbs :items="breadCrumbs" class="px-0 pt-0 pb-5" />
  <h4 class="text-h4 my-4">{{ pageTitle }}</h4>
  <VCard>
    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :headers="headers"
      :items="dataItems"
      :items-length="totalItems"
      :loading="loadingTable"
      class="text-no-wrap"
      @update:options="updateOptions"
    >
      <template #top>
        <div>
          <VForm
            ref="refForm"
            @submit.prevent="
              () => {
                searchResult();
              }
            "
          >
            <VRow
              class="ms-4 me-4 my-2 justify-center justify-sm-space-between"
            >
              <VCol cols="12" lg="6">
                <VRow class="align-center">
                  <VCol cols="10">
                    <AppTextField
                      v-model="searchByMerchantId"
                      label="Merchant id"
                      readonly
                    />
                  </VCol>
                  <VCol class="pt-9" cols="2">
                    <VBtn
                      block
                      color="primary"
                      type="button"
                      @click="referMerchantClick(true)"
                      >Refer
                    </VBtn>
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12" lg="6">
                <VRow class="align-center">
                  <VCol cols="10">
                    <AppTextField
                      v-model="searchByShopId"
                      label="Shop Id"
                      readonly
                    />
                  </VCol>
                  <VCol class="pt-9" cols="2">
                    <VBtn
                      :disabled="searchByMerchantId ? false : true"
                      block
                      color="primary"
                      type="button"
                      @click="referShopClick(true)"
                      >Refer
                    </VBtn>
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12" lg="6">
                <VRow class="align-center">
                  <VCol cols="10">
                    <AppTextField
                      v-model="searchByLocationId"
                      label="Location ID"
                      readonly
                    />
                  </VCol>
                  <VCol class="pt-9" cols="2">
                    <VBtn
                      :disabled="
                        searchByMerchantId && searchByShopId ? false : true
                      "
                      block
                      color="primary"
                      type="button"
                      @click="referLocationClick(true)"
                      >Refer
                    </VBtn>
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12" lg="6">
                <AppTextField
                  v-model="searchByName"
                  label="Location Name"
                  placeholder="Input Location Name"
                />
              </VCol>
              <VCol cols="12" lg="6">
                <AppSelect
                  v-model="searchByStatus"
                  :items="statuses"
                  label="Status"
                  placeholder="Choose Status"
                />
              </VCol>
            </VRow>
            <div>
              <VCardText
                class="d-flex align-center flex-wrap justify-space-between gap-4"
              >
                <VSpacer />
                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  color="primary"
                  type="submit"
                >
                  Search

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
                <VBtn
                  :disabled="loadingClear"
                  :loading="loadingClear"
                  color="secondary"
                  type="reset"
                  variant="tonal"
                  @click="refreshResult()"
                >
                  <VIcon icon="tabler-refresh" start />
                  Clear
                </VBtn>
                <VSpacer />
              </VCardText>
            </div>
          </VForm>
        </div>

        <div>
          <VRow class="ms-4 me-4 my-2">
            <VCol cols="12" lg="2" sm="2">
              <AppSelect
                v-model="itemsPerPage"
                :items="perPages"
                class="ml-auto"
                placeholder="Per Page"
              />
            </VCol>
            <VCol cols="12" lg="3" sm="3">
              <VBtn :to="{ path: 'location/create' }" block color="primary">
                <VIcon icon="tabler-plus" start />
                Create {{ pageTitle }}
              </VBtn>
            </VCol>
          </VRow>
        </div>
      </template>
      <template #item.status="{ item }">
        <AppSelect
          v-model="item.status"
          :items="statuses"
          class="v-select-print"
          readonly
        />
      </template>
      <template #item.lastOpen="{ item }">
        {{ item.lastOpen ? moment(item.lastOpen).format("LLL") : null }}
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <IconBtn :to="{ path: 'location/view/' + item.id }">
            <VIcon icon="tabler-edit" />
          </IconBtn>
          <IconBtn @click="deleteItem(item.id, item.name)">
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </div>
      </template>
      <template #bottom>
        <VCardText class="pt-2">
          <div
            class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2"
          >
            <div></div>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalItems / itemsPerPage)"
              :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            />
          </div>
        </VCardText>
      </template>
    </VDataTableServer>
  </VCard>
  <!-- Dialog -->
  <VDialog v-model="referMerchant" max-width="500px">
    <DialogCloseBtn @click="referMerchantClick(false)" />
    <VCard class="pa-5 pa-sm-8">
      <MerchantRefer
        grab="id"
        @set-merchant-refer="
          (e) => (
            (searchByMerchantId = e),
            (searchByShopId = null),
            (searchByLocationId = null)
          )
        "
      />
    </VCard>
  </VDialog>
  <VDialog v-model="referShop" max-width="500px">
    <DialogCloseBtn @click="referShopClick(false)" />
    <VCard class="pa-5 pa-sm-8">
      <ShopRefer
        :merchant="searchByMerchantId"
        grab="id"
        @set-shop-refer="
          (e) => ((searchByShopId = e), (searchByLocationId = null))
        "
      />
    </VCard>
  </VDialog>
  <VDialog v-model="referLocation" max-width="500px">
    <DialogCloseBtn @click="referLocationClick(false)" />
    <VCard class="pa-5 pa-sm-8">
      <LocationRefer
        :merchant="searchByMerchantId"
        :shop="searchByShopId"
        grab="id"
        @set-location-refer="(e) => (searchByLocationId = e)"
      />
    </VCard>
  </VDialog>
  <!-- 👉 Delete Dialog  -->
  <VDialog v-model="deleteDialog" max-width="500px">
    <VCard v-show="!deleteAlert">
      <VCardTitle class="text-center my-3">
        <p>Are you sure you want to delete this item?</p>
        <h3>{{ deleteDialogDisplay }}</h3>
      </VCardTitle>

      <VCardActions>
        <VSpacer />
        <VBtn color="secondary" variant="outlined" @click="deleteCancel">
          Cancel
        </VBtn>
        <VBtn color="error" variant="elevated" @click="deleteOk(deleteDialog)">
          OK
        </VBtn>
        <VSpacer />
      </VCardActions>
    </VCard>
    <VCard v-show="deleteAlert">
      <VCardText>
        <VAlert type="error">
          {{ deleteAlert }}
        </VAlert>
      </VCardText>
    </VCard>
  </VDialog>
</template>
